import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";

import { isSessionEnded, sessionMark } from "stores/sessionGuard";
/**
 * Shape returned by the admin-gated 'api-keys' custom service
 * (packages/api/src/services/system/api-keys/api-keys.ts). The bcrypt hash is
 * never exposed; lists carry `keyMasked` and the plaintext `key` exists ONLY
 * on the create response — it is never refetchable.
 */
export interface ApiKeyRecord {
  id: number;
  /**
   * null = an INTERNAL key: one IntraSync holds itself, not minted on behalf of
   * a client (the read-only /mcp endpoint called from an agent session is the
   * case this exists for). Nullable in the DB since API migration
   * 20260817000001. It is an inventory LABEL, not a grant — the API reads
   * `api_keys.clientId` for display only and authorizes nothing on it.
   */
  clientId: string | null;
  keyName: string;
  keyPrefix: string;
  keyMasked?: string;
  createdBy?: number | null;
  scopes?: string[] | null;
  lastUsedAt?: string | null;
  lastUsedIp?: string | null;
  expiresAt?: string | null;
  revokedAt?: string | null;
  revokedBy?: number | null;
  usageCount?: number;
  /** Per-surface uses (API `usage-kinds.ts`). null = NOT YET OBSERVED, never "one consumer". */
  usageByKind?: UsageByKind | null;
  createdAt?: string;
  updatedAt?: string;
  status?: "active" | "revoked" | "expired";
  // Present only on the create response
  key?: string;
  warning?: string;
}

interface ClientSummary {
  id: string;
  name?: string;
  code?: string;
}

/** Given only at mint; see api-keys.ts CLIENT_BOUND_SCOPE_MINT_ONLY. */
export const MINT_ONLY_SCOPE = "client-inquiries:answer";

/**
 * Every scope given only at MINT, with the key's client set — the API's
 * CLIENT_BOUND_SCOPES (scopes.ts): the key acts as that client's instance, so
 * it never gains the scope later and never changes client.
 */
export const MINT_ONLY_SCOPES: readonly string[] = [MINT_ONLY_SCOPE, "feedback:intake"];

/**
 * Scopes that are a key's ONLY scope for as long as it lives — the API's
 * SOLE_PURPOSE_SCOPES. Such a key is refused everywhere in HQ but its one route,
 * its scope list cannot be edited, and the scope is given only at mint (the API
 * refuses adding one to an existing key).
 */
export const SOLE_SCOPES: readonly string[] = ["feedback:intake", "coordinator:publish"];

/** Every scope an edit may not ADD: the client-bound ones and the sole ones (both are given only at mint). */
export const EDIT_LOCKED_SCOPES: readonly string[] = [...new Set([...MINT_ONLY_SCOPES, ...SOLE_SCOPES])];

export type ApiKeyStatusFilter = "all" | "active" | "revoked" | "expired";

/**
 * How a key with no client reads.
 *
 * Two spellings on purpose. The picker states the whole meaning, because that
 * is the moment an operator decides; the table and the CSV need a short label
 * that fits a column. Both say "Internal" — never an em-dash, which is this
 * view's spelling for "missing/unknown" and would put a real, deliberate
 * category back into the bucket of things that look like a data gap.
 */
export const INTERNAL_CLIENT_OPTION_LABEL = "Internal — no client";
export const INTERNAL_CLIENT_LABEL = "Internal";

/** The `form.clientId` value that means "no client". */
export const INTERNAL_CLIENT_VALUE = "";

/**
 * The scopes the API ACTUALLY ENFORCES, and where.
 *
 * ── WHY THIS LIST IS THE WHOLE STORY ───────────────────────────────────────
 *
 * `api_keys.scopes` was stored, returned and enforced nowhere, so this view
 * removed its Scopes column on 2026-08-05: rendering the value as chips made
 * it read as an access control, and a key labelled `read:meetings` looked
 * narrower than an unscoped one while having identical authority. The comment
 * left behind said: bring it back when a hook enforces a scope — and bring the
 * UI and the gate in the same change.
 *
 * That hook now exists. `meeting-analysis:produce` is checked by
 * `require-producer.ts` on the two Gantry handoff services, and a key without
 * it is refused there. It is the ONLY enforced scope. Every other string a key
 * carries is still a label that nothing reads, and the view says so on every
 * chip rather than letting a label borrow the look of a permission.
 *
 * Add to this list ONLY alongside the hook that enforces the scope. A scope
 * listed here that nothing checks is precisely the lie the 2026-08-05 removal
 * was for.
 */
export const ENFORCED_SCOPES: ReadonlyArray<{
  scope: string;
  /** Which services refuse a key without it. */
  enforcedBy: string;
  /** What holding it buys, stated as a blast radius. */
  description: string;
}> = [
  {
    scope: "meeting-analysis:produce",
    enforcedBy:
      "meeting-analysis-queue, meeting-analysis-result, the queue event stream, the Read.ai source proxy, meeting-evidence uploads",
    description:
      "Act as a Gantry analysis producer: poll, claim and push meeting analysis, follow the queue event stream, fetch Read.ai sources through HQ, and attach evidence frames to a meeting. Everywhere else the key stays read-only.",
  },
  {
    scope: "action-items:complete",
    enforcedBy: "developer-action-items (patch)",
    description:
      "Let a developer's coder instance mark one of ITS OWN accepted action items completed, with a PR link, which also closes that item's Azure DevOps work item and records the PR on it. It cannot complete anyone else's, and cannot do anything else.",
  },
  {
    scope: "action-items:verify",
    enforcedBy: "action-item-verifications (create)",
    description:
      "Let a fleet sweeper (Gantry) file completion EVIDENCE — commit citations — against any assigned action item. Evidence is a proposal a human confirms; this scope completes nothing and writes nothing else.",
  },
  {
    scope: "meetings:read",
    enforcedBy: "developer-meetings (find, get)",
    description:
      "Let a developer's own assistant instance read the meetings THAT DEVELOPER attended or owns, with the full transcript. It cannot see a meeting they were not in, cannot reach the recording URL, and writes nothing. The identity is the human who MINTED the key — a key minted by an admin returns the admin's meetings.",
  },
  {
    scope: "tickets:read",
    enforcedBy: "ticket-feed (find), tickets (find, get), ticket-events (find)",
    description:
      "Let the key's own user's Gantry poll the events on tickets assigned to them, routed to their team, or filed by them — only the kinds that person switched on for the Gantry leg under Settings → Notifications — and read those tickets and their timelines, exactly what that person may see in the web. Nothing else becomes readable, and nothing is written. A key without it cannot read tickets at all, whoever minted it. ONE WIDENING: held together with meeting-analysis:produce, `tickets?scope=all` also lists the feedback kinds (feedback, request, idea, review, bug) fleet-wide, because a producer lifts them the way it lifts a meeting. No other kind, and `get` is unchanged. Without meeting-analysis:produce, `tickets?scope=all` is refused with a 403 that says so — use scope=mine, asked or team.",
  },
  {
    scope: "tickets:act",
    enforcedBy: "tickets (patch)",
    description:
      "Let the key's own user's Gantry acknowledge, comment on, start or finish a ticket AS THAT PERSON, or record the lift it opened from one — those five actions only, and never more than that person may do in the web. It cannot file, reassign, decline, cancel or reopen a ticket, or touch anyone else's. Includes tickets:read.",
  },
  {
    scope: "tickets:file",
    enforcedBy:
      "tickets (create), POST /documents/upload (attachments to the filer's own tickets only)",
    description:
      "Let the key's own user's assistant FILE a ticket AS THAT PERSON — feedback, a request, an idea, a review or a bug about a client or a governed module, and no other kind. It names what the ticket is about and never who gets it: HQ routes it. It may attach images or markdown to a ticket that person filed. It reads nothing and acts on nothing; note the identity is the human who MINTED the key, so mint it from the account of the person whose assistant will hold it.",
  },
  {
    scope: "reports:send",
    enforcedBy: "report-intake",
    description:
      "Let a producer (Gantry) post a report-kit spec for HQ to render and mail — the weekly summary and the occasional one-off. It chooses the content, never the recipients: HQ sends only to staff who switched that report kind on for the email leg themselves, so a key holding this cannot mail anyone who has not opted in, and cannot mail a client at all. It reads nothing.",
  },
  {
    scope: "client-inquiries:answer",
    enforcedBy: "v1/client-inquiries/answers",
    description:
      "Let a client's own instance file that client's answer to a client inquiry. Given only when a key is minted, with the client set, and a key holding it can never change client: the answer is filed under the key's client and refused if it names another. It files answers only — it reads nothing, and it cannot change an answer already recorded.",
  },
  {
    scope: "client-inquiries:file",
    enforcedBy:
      "client-inquiries.create, client-inquiries.patch with { action: 'answer' | 'attach' | 'detach' }, and { action: 'withdraw' } while the inquiry is still internal",
    description:
      "Let Gantry FILE a question it cannot settle as a client inquiry at the INTERNAL stage, record an internal answer (or a client's emailed answer) on one, attach or detach the lift it came from, and withdraw one nobody has released. Nothing it does reaches a client: the record stays inside HQ until a person releases it. It reads nothing without client-inquiries:read.",
  },
  {
    scope: "client-inquiries:read",
    enforcedBy: "client-inquiries.find, client-inquiries.get",
    description:
      "Let Gantry read client-inquiry records — the question, its stage, the internal answers and who released it — for every client. It writes nothing, and it does NOT open the client-inquiry answers that client instances file, which no API key may read.",
  },
  {
    scope: "client-inquiries:release",
    enforcedBy: "client-inquiries.patch with { action: 'release' }, and { action: 'withdraw' } once released",
    description:
      "Let Gantry RELEASE a client inquiry to the client after a person taps it in the Cab, or withdraw a released one — the only outward-facing acts on this record. HQ does not publish: a release records that a person sent the question (manual delivery). It works only when the key's MINTER may release (an admin, or a role on the release list), and the record names the minter as the releaser, with whoever Gantry says tapped it kept beside it as a claim.",
  },
  {
    scope: "person-messages:send",
    enforcedBy: "person-messages (create, get)",
    description:
      "Let the key's own user's assistant SEND a short message to one active staff member AS THAT PERSON — it lands in the recipient's bell, and on their phone or Gantry only if they switched that on themselves. The sender is always the human who MINTED the key and can never be named in the request; it is limited to 30 messages an hour and 10 to any one person. It may read back whether a message it sent has been read, and nothing else.",
  },
  {
    scope: "notifications:read-own",
    enforcedBy: "notification-feed (find)",
    description:
      "Let the key's own user's Gantry collect the messages colleagues sent THAT PERSON — only once the person has switched \"Message from a colleague\" on for the Gantry leg under Settings → Notifications. It reads nobody else's messages, no tickets, and writes nothing.",
  },
  {
    scope: "developer-metrics:produce",
    enforcedBy: "developer-metrics-intake",
    description:
      "Let a producer (Gantry) post one run of developer metrics — commits, lines, work split, AI-assisted share and code survival per developer, repo and week — for HQ to store. HQ maps the git emails to people itself, from Admin → Git Identities; the key cannot name a person, cannot change or delete a run already posted (a repeat of a run id is answered from the record and writes nothing), and reads nothing back.",
  },
  {
    scope: "action-items:retarget",
    enforcedBy: "developer-action-items (patch, retarget and retarget-undo)",
    description:
      "Let the key's own user's Gantry (Chaz, its software manager) move one of THAT PERSON'S ACCEPTED action items to another governed module or client, with a reason and evidence, and undo that move within 14 days. Every move is recorded with who and why, and the previous target is kept. It cannot accept, complete or route anyone else's item, and it does not move an Azure DevOps work item already filed.",
  },
  {
    scope: "feedback:intake",
    enforcedBy: "POST /v1/feedback",
    description:
      "Let a client's own instance send its users' in-app feedback to HQ, where each submission becomes a ticket on the engineering queue about the key's client. Given only when a key is minted, with the client set, and as the key's ONLY scope; the key can never change client or scopes. It is refused everywhere else in HQ — it reads nothing, not even the tickets it filed.",
  },
  {
    scope: "coordinator:publish",
    enforcedBy: "POST /v1/coordinator-snapshots",
    description:
      "Let one coordinator's publisher (the Client, Internal or Module Coordinator, on the box that runs its heartbeat) post its read-only snapshot to HQ, which HQ then serves to every session through the MCP endpoint. Minted as the key's ONLY scope, with no client; the first snapshot it posts binds it to that coordinator, so it can never post another coordinator's. It is refused everywhere else in HQ — it reads nothing, not even the snapshots it posted.",
  },
];

const ENFORCED_SCOPE_SET: ReadonlySet<string> = new Set(ENFORCED_SCOPES.map((s) => s.scope));

/** True when something in the API refuses a key that lacks this scope. */
export const isEnforcedScope = (scope: string): boolean => ENFORCED_SCOPE_SET.has(scope);

/**
 * Parse what an operator typed into a scope list: comma, whitespace or newline
 * separated, trimmed, de-duplicated, empties dropped. Order is kept so the
 * stored array reads the way it was entered.
 */
export function parseScopeInput(raw: string): string[] {
  const out: string[] = [];
  for (const part of raw.split(/[,\s]+/)) {
    const scope = part.trim();
    if (scope && !out.includes(scope)) out.push(scope);
  }
  return out;
}

/** The scopes on a record that are actually enforced. */
export const enforcedScopesOf = (record: Pick<ApiKeyRecord, "scopes">): string[] =>
  (record.scopes ?? []).filter(isEnforcedScope);

/** One de-duplicated list from the enforced checkboxes plus the free-text labels. */
const mergeScopes = (enforced: string[], labels: string): string[] =>
  parseScopeInput([...enforced, ...parseScopeInput(labels)].join(","));

/**
 * The server caps `api-keys` find at 500 rows (`Math.min(Number(query.$limit)
 * || 200, 500)` in api-keys.ts) and returns a BARE ARRAY with no `total`. So
 * 500 is both the most we can ask for and the point at which the list silently
 * stops being the whole inventory — see `ApiKeysStore.truncated`.
 */
export const API_KEYS_SERVER_LIMIT = 500;

/**
 * The CSV projection of one API key: an ACCESS INVENTORY row.
 *
 * Every field is listed explicitly and nothing is spread from the source
 * record. That is the whole point: `ApiKeyRecord` can carry the one-time
 * plaintext `key` (the create response sets it) and callers may hand this
 * function whatever they have, so a spread — or an `Omit<>`-shaped passthrough
 * — would put a live credential into a file that leaves the machine. The
 * allow-list below cannot leak a field it does not name.
 *
 * `keyMasked` is the value the SERVICE masked (`keyPrefix.slice(0,8) + '…' +
 * keyPrefix.slice(-4)`), falling back to the stored `keyPrefix`; both are
 * derived from the key's first 12 characters and neither is sufficient to
 * reconstruct or use a key. The bcrypt `keyHash` is never sent by the API at
 * all, and is never read here.
 */
/** One kind of use, as the API records it (`api_keys.usageByKind`). */
export interface UsageKindEntry {
  count: number;
  firstAt: string;
  lastAt: string;
}

/** Keyed by kind: `mcp`, `service:<path>`, `route:<name>`, `unlabelled`, `other`. */
export type UsageByKind = Record<string, UsageKindEntry>;

export interface UsageKindRow extends UsageKindEntry {
  kind: string;
  label: string;
}

/**
 * What a revoker can honestly be told about where a key is used.
 *
 * The API's reading rules (packages/api/.../api-keys/usage-kinds.ts), which
 * this must not soften:
 * - null/empty is NOT YET OBSERVED. Every key minted before the column existed,
 *   and any key unused since, reads that way. It never means "one consumer",
 *   and never "nothing uses it".
 * - The map speaks only for the window since its earliest `firstAt`. A consumer
 *   idle longer than that is invisible, so a key showing one kind is "one kind
 *   seen since <date>", never "one consumer".
 * - A kind is a SURFACE, not a machine: two consumers on the same surface are
 *   one kind.
 */
export type UsageSummary =
  | { observed: false }
  | { observed: true; since: string; kinds: UsageKindRow[] };

/** Human label for a recorded kind. Unknown shapes are shown verbatim. */
export const usageKindLabel = (kind: string): string => {
  if (kind === "mcp") return "MCP (read-only endpoint)";
  if (kind === "unlabelled") return "Unlabelled (no surface named itself)";
  if (kind === "other") return "Other (beyond the 32 kinds kept)";
  if (kind.startsWith("service:")) return `REST: ${kind.slice("service:".length)}`;
  if (kind.startsWith("route:")) return `Route: ${kind.slice("route:".length)}`;
  return kind;
};

/** Pure: the per-kind map as rows, most recently used first. */
export function summariseUsageByKind(
  usage: UsageByKind | null | undefined
): UsageSummary {
  const entries = Object.entries(usage ?? {}).filter(
    ([, e]) =>
      e &&
      Number.isFinite(e.count) &&
      typeof e.firstAt === "string" &&
      typeof e.lastAt === "string"
  );
  if (entries.length === 0) return { observed: false };
  const kinds = entries
    .map(([kind, e]) => ({ kind, label: usageKindLabel(kind), ...e }))
    .sort((a, b) => b.lastAt.localeCompare(a.lastAt));
  const since = kinds
    .map((k) => k.firstAt)
    .reduce((min, t) => (t < min ? t : min));
  return { observed: true, since, kinds };
}

/** One-line text form, for the CSV. Says "not yet observed", never "none". */
export function usageSummaryText(summary: UsageSummary): string {
  if (!summary.observed) return "not yet observed";
  return `${summary.kinds.map((k) => `${k.label} (${k.count})`).join("; ")} — since ${summary.since}`;
}

export interface ApiKeyExportRow extends Record<string, unknown> {
  keyName: string;
  keyMasked: string;
  client: string;
  status: string;
  scopesEnforced: string;
  createdAt: string;
  lastUsedAt: string;
  lastUsedIp: string;
  expiresAt: string;
  revokedAt: string;
  usageCount: number;
  usedFrom: string;
}

/**
 * Shape one key record into its export row. Pure: takes the already-resolved
 * client display name rather than reaching for a store, so the masking rule is
 * testable on its own.
 */
export function toApiKeyExportRow(
  record: ApiKeyRecord,
  clientName: string
): ApiKeyExportRow {
  return {
    keyName: record.keyName ?? "",
    keyMasked: record.keyMasked || record.keyPrefix || "",
    client: clientName,
    status: record.status ?? "active",
    // ONLY the scopes something in the API actually checks. A label a key
    // carries that nothing reads must not land in a compliance artefact
    // looking like a restriction — so `billing:read` is not exported and
    // `meeting-analysis:produce` is. "none" when the key has no enforced scope.
    scopesEnforced: enforcedScopesOf(record).join(", ") || "none",
    createdAt: record.createdAt ?? "",
    lastUsedAt: record.lastUsedAt ?? "",
    lastUsedIp: record.lastUsedIp ?? "",
    expiresAt: record.expiresAt ?? "",
    revokedAt: record.revokedAt ?? "",
    usageCount: record.usageCount ?? 0,
    usedFrom: usageSummaryText(summariseUsageByKind(record.usageByKind)),
  };
}

export type ApiKeySortKey =
  | "keyName"
  | "clientName"
  | "status"
  | "createdAt"
  | "lastUsedAt"
  | "expiresAt";

/** Sort keys that read best newest-first on first click. */
const DESC_FIRST: ReadonlySet<ApiKeySortKey> = new Set([
  "createdAt",
  "lastUsedAt",
  "expiresAt",
]);

/**
 * View store for the /admin/api-keys management view.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do
 * NOT extend ViewStore. The api-keys service is a custom admin-only service
 * (non-paginated find returning a bare array). It is now read through the
 * registered `api-keys` ServiceStore (see serviceStores/admin.stores.ts):
 * `find` populates the store's entity cache, which the `keys` computed view
 * reads; create/revoke/delete go through the same store.
 *
 * DIALOGS ARE ROUTE-REGISTERED (apiKeys.Route.ts `dialogs`), not rendered by the
 * body. This store holds what each dialog SHOWS (`form`, `createdKey`,
 * `confirmAction`) and calls `routerStore.openDialog` to raise one; the router
 * owns open/closed and supplies the standard chrome plus `forceFullScreenDialog`
 * on phones.
 */
class ApiKeysStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;

  // Filters
  statusFilter: ApiKeyStatusFilter = "all";
  searchQuery = "";

  // Column sort (applied client-side over the filtered set, like the filters)
  sortKey: ApiKeySortKey = "createdAt";
  sortDir: SortDir = "desc";

  // Client directory load guard (the list itself is a computed view over the
  // clients ServiceStore — see the `clients` getter).
  private clientsLoaded = false;

  // Create dialog state. There is deliberately no `createDialogOpen` here: the
  // dialogs are route-registered (apiKeys.Route.ts) and the router owns whether
  // one is open. A second boolean would be a copy of that truth that drifts.
  creating = false;
  createError: string | null = null;
  form = {
    clientId: "",
    keyName: "",
    /** Free-text LABELS, comma-separated. Enforced scopes are the checkboxes
     *  in `formEnforcedScopes`; both are merged on submit. */
    scopes: "",
    expiresAt: "", // YYYY-MM-DD, optional
  };
  /** Enforced scopes ticked on the create form. */
  formEnforcedScopes: string[] = [];

  // Edit-scopes dialog state. Same route-registered pattern as the others: the
  // store holds what the dialog shows and the router owns whether it is open.
  scopesTarget: ApiKeyRecord | null = null;
  scopesDraftEnforced: string[] = [];
  scopesDraftLabels = "";
  scopesError: string | null = null;
  savingScopes = false;

  /**
   * The one-time plaintext key from the last successful create. Shown ONCE in
   * a dedicated dialog with a copy button; the API never returns it again.
   */
  createdKey: ApiKeyRecord | null = null;
  keyCopied = false;

  // Revoke / delete confirmation state
  confirmAction: { type: "revoke" | "delete"; record: ApiKeyRecord } | null = null;
  /** Second action for the irreversible one — see `isDeleteConfirmed`. */
  deleteAcknowledged = false;
  actioning = false;
  /** Why the last revoke/delete failed — the route throws it into the dialog's footer. */
  actionError: string | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `api-keys` ServiceStore (see serviceStores/admin.stores.ts). */
  private get apiKeysService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("api-keys") ?? this.rootStore.apiKeysStore) as unknown as ServiceStoreType | null) ?? null;
  }

  /**
   * The `clients` ServiceStore (owned by the clients domain). Best-effort: used
   * only for the directory lookup, so a null store leaves rows on their raw id.
   */
  private get clientsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("clients") ?? this.rootStore.clientsStore) as unknown as ServiceStoreType | null) ?? null;
  }

  // ---- Derived data -------------------------------------------------------

  /**
   * The API-key list. Per the MobX rule, entities live in the ServiceStore
   * cache (populated by `loadKeys`'s `find`), not in this ViewStore — this is a
   * computed view.
   */
  get keys(): ApiKeyRecord[] {
    return (this.apiKeysService?.entities ?? []) as unknown as ApiKeyRecord[];
  }

  /**
   * The table shows its spinner only while there is nothing to show yet.
   * Creating, revoking or deleting a key reloads the list, and swapping the
   * rows out for a spinner during that reload shrank the page under you and
   * left you at the top of it. The rows stay up while they refresh, as on
   * every other list page.
   */
  get initialLoading(): boolean {
    return this.loading && this.keys.length === 0;
  }

  /**
   * Client directory (id -> name) for display + the create dialog. A computed
   * view over the clients ServiceStore cache (populated by `loadClients`).
   */
  get clients(): ClientSummary[] {
    return (this.clientsService?.entities ?? []) as unknown as ClientSummary[];
  }

  get filteredKeys(): ApiKeyRecord[] {
    const query = this.searchQuery.trim().toLowerCase();
    return this.keys.filter((record) => {
      if (this.statusFilter !== "all" && record.status !== this.statusFilter) {
        return false;
      }
      if (!query) return true;
      const haystack = [
        record.keyName,
        record.keyPrefix,
        this.clientName(record.clientId),
        ...(record.scopes || []),
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }

  /**
   * The rows the table renders: filters, then the active column sort. Nulls
   * (never used, no expiry) sort last in either direction.
   *
   * A key with NO CLIENT is deliberately NOT a null here: it sorts under
   * "Internal", the same string the column shows. It used to sort as null —
   * always last — which was right while a missing clientId meant a data gap and
   * is wrong now that it is a real category an operator picks on purpose.
   */
  get sortedKeys(): ApiKeyRecord[] {
    const key = this.sortKey;
    const valueOf = (record: ApiKeyRecord): SortValue => {
      switch (key) {
        case "keyName":
          return textValue(record.keyName);
        case "clientName":
          // Sort by the RESOLVED display name, matching what the column shows.
          return textValue(this.clientName(record.clientId));
        case "status":
          return textValue(record.status ?? "active");
        case "createdAt":
          return dateValue(record.createdAt);
        case "lastUsedAt":
          return dateValue(record.lastUsedAt);
        case "expiresAt":
          return dateValue(record.expiresAt);
      }
    };
    return sortRows(this.filteredKeys, valueOf, this.sortDir);
  }

  /**
   * Display name for a key's client. No client is INTERNAL — a real category,
   * stated — not an em-dash, which in this view means "missing/unknown".
   */
  clientName(clientId: string | null | undefined): string {
    if (!clientId) return INTERNAL_CLIENT_LABEL;
    const client = this.clients.find((c) => c.id === clientId);
    return client?.name || clientId;
  }

  /**
   * A client is OPTIONAL — only the name is required. Demanding a client is
   * exactly what made an internal/operator key (e.g. one to call HQ's own /mcp
   * endpoint) impossible to mint from this screen.
   */
  get canSubmitCreate(): boolean {
    return Boolean(this.form.keyName.trim()) && !this.creating && this.formScopeError === null;
  }

  /**
   * Why the create form's scopes would be refused, before the API is asked:
   * a client-bound scope needs its client set, and a sole scope must be the
   * key's only one (the API's CLIENT_BOUND_SCOPES / SOLE_PURPOSE_SCOPES).
   */
  get formScopeError(): string | null {
    const scopes = this.formScopes ?? [];
    const bound = scopes.find((s) => MINT_ONLY_SCOPES.includes(s));
    if (bound && !this.form.clientId) {
      return `${bound} needs its client set: the key acts as that client's instance. Pick the client above.`;
    }
    const sole = scopes.find((s) => SOLE_SCOPES.includes(s));
    if (sole && scopes.length > 1) {
      return `${sole} is a key's only scope: untick every other scope and clear the labels, and mint a separate key for anything else.`;
    }
    return null;
  }

  /**
   * True when the loaded list may not be the whole inventory. `api-keys` find
   * returns a bare array capped server-side at 500 with no `total`, so a full
   * page back is indistinguishable from "exactly 500 keys exist" — the only
   * honest reading is "500 or more". An access inventory that quietly omits
   * keys is worse than one that admits it is partial, so the view says so and
   * the CSV filename records it.
   */
  get truncated(): boolean {
    return this.keys.length >= API_KEYS_SERVER_LIMIT;
  }

  /**
   * The filtered + sorted keys flattened to plain rows for CSV export — an
   * access inventory (who holds a key, for which client, active or revoked,
   * last used when), never key material. Reads `sortedKeys` so the file carries
   * exactly the rows and order on screen.
   */
  get exportRows(): ApiKeyExportRow[] {
    return this.sortedKeys.map((record) =>
      toApiKeyExportRow(record, this.clientName(record.clientId))
    );
  }

  /** Filename for the CSV, stamped as partial when the server cap was hit. */
  get exportFileName(): string {
    const stamp = new Date().toISOString().slice(0, 10);
    return this.truncated
      ? `api-keys_first-${API_KEYS_SERVER_LIMIT}_${stamp}`
      : `api-keys_${stamp}`;
  }

  // ---- Filters ------------------------------------------------------------

  setStatusFilter(value: ApiKeyStatusFilter) {
    this.statusFilter = value;
  }

  setSearchQuery(value: string) {
    this.searchQuery = value;
  }

  /** Column-header click: same column toggles direction, a new column starts
   *  in its natural direction (dates newest-first, text A→Z). */
  setSort(key: ApiKeySortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  // ---- Create dialog ------------------------------------------------------
  //
  // The three dialogs are REGISTERED ON THE ROUTE (apiKeys.Route.ts `dialogs`),
  // not rendered inline by the body, so they inherit the router's standard
  // chrome and its `forceFullScreenDialog` on phones. This store therefore owns
  // the dialogs' DATA and asks the router to open them; it never owns an `open`
  // boolean, and the framework — not this store — closes them.

  private openDialog(dialogName: string) {
    this.rootStore.routerStore?.openDialog?.({ dialogName });
  }

  openCreateDialog() {
    this.form = {
      clientId: INTERNAL_CLIENT_VALUE,
      keyName: "",
      scopes: "",
      expiresAt: "",
    };
    this.formEnforcedScopes = [];
    this.createError = null;
    this.openDialog("CreateApiKeyDialog");
  }

  setFormField(field: keyof ApiKeysStore["form"], value: string) {
    this.form[field] = value;
  }

  /** Tick or untick an enforced scope on the create form. */
  toggleFormScope(scope: string) {
    this.formEnforcedScopes = this.formEnforcedScopes.includes(scope)
      ? this.formEnforcedScopes.filter((s) => s !== scope)
      : [...this.formEnforcedScopes, scope];
  }

  /** What the create form would store, or null for "no scopes". */
  get formScopes(): string[] | null {
    const merged = mergeScopes(this.formEnforcedScopes, this.form.scopes);
    return merged.length ? merged : null;
  }

  /**
   * @returns true when the key was created. The route's onSubmit uses this to
   *   decide between chaining to the one-time reveal and throwing `createError`,
   *   which keeps this dialog open with the error and the typed values intact.
   */
  async submitCreate(): Promise<boolean> {
    if (!this.canSubmitCreate) return false;
    // The create reaches the server even if the person signs out while it is in
    // flight, and the session guard then rejects the client promise instead of
    // handing the plaintext key back. This mark is the belt to that brace: a
    // key from a session that ended is never assigned to `createdKey`, never
    // opens the reveal dialog (the route chains to it only on `true`), and its
    // creation is not reported as this next person's error. The key exists
    // server-side and shows in the list for the owner to revoke.
    const ended = sessionMark(this.rootStore);
    this.creating = true;
    this.createError = null;
    try {
      // `scopes` is sent again. From 2026-08-05 this was hard-coded null,
      // because the column was enforced nowhere and a stored scope was a false
      // record of a restriction. `meeting-analysis:produce` is now enforced
      // (see ENFORCED_SCOPES), so the input is back — with the gate, in the
      // same change, as that comment asked.
      const payload: Record<string, unknown> = {
        // null, not "". The picker's "Internal — no client" choice carries the
        // empty string (a MUI Select needs a defined value), and the API stores
        // NULL for absent/null/"" alike — but sending an explicit null is what
        // makes "no client" a stated intent on the wire rather than something
        // inferred from a blank.
        clientId: this.form.clientId || null,
        keyName: this.form.keyName.trim(),
        scopes: this.formScopes,
        expiresAt: this.form.expiresAt
          ? `${this.form.expiresAt}T23:59:59.999Z`
          : null,
      };
      const record = (await this.apiKeysService?.create(
        payload as never,
      )) as unknown as ApiKeyRecord;
      if (ended()) return false;
      runInAction(() => {
        // Surface the one-time plaintext key. It is intentionally NOT stored
        // in the list (rows come from reload and never carry `key`).
        this.createdKey = record;
        this.keyCopied = false;
      });
      // NO success toast here, deliberately. The reveal dialog that opens next
      // IS the confirmation, and on a 375px phone the dialog goes full-screen
      // while the snackbar renders on top of it — measured 2026-08-17: the
      // toast landed squarely over "I've stored the key" and did not auto-hide,
      // so the only action on the only screen that will ever show the plaintext
      // key was unclickable. Revoke and delete keep their toasts: they have no
      // follow-up dialog, so the toast is the only feedback they get.
      await this.loadKeys();
      return true;
    } catch (err) {
      if (ended()) return false;
      runInAction(() => {
        this.createError = err instanceof Error ? err.message : "Failed to create API key";
      });
      return false;
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.creating = false;
        });
      }
    }
  }

  async copyCreatedKey() {
    const key = this.createdKey?.key;
    if (!key) return;
    const ended = sessionMark(this.rootStore);
    try {
      await navigator.clipboard.writeText(key);
      // The clipboard is no Feathers call, so the session guard never ends it:
      // "copied" must not mark a reveal dialog the purge has already closed.
      if (ended()) return;
      runInAction(() => {
        this.keyCopied = true;
      });
    } catch {
      // A sign-out while the browser was asked: the key, and the dialog it was
      // in, went with the purge; there is nothing to tell the next person.
      if (ended()) return;
      this.rootStore.notificationStore?.addWarningNotification(
        "Could not access the clipboard — copy the key manually."
      );
    }
  }

  dismissCreatedKey() {
    this.createdKey = null;
    this.keyCopied = false;
  }

  /** Chain the create dialog into the one-time reveal (see apiKeys.Route.ts). */
  openCreatedKeyDialog() {
    this.openDialog("RevealApiKeyDialog");
  }

  // ---- Edit scopes --------------------------------------------------------
  //
  // The reason this dialog exists: granting the Gantry producer key its scope
  // otherwise means a shell on the API host (`yarn api key:grant-scope`), and
  // the operator is usually on a phone. The service's patch REPLACES the scope
  // array wholesale, so the dialog always starts from the key's current list
  // and submits the whole list — an enforced scope cannot be dropped by
  // accident while adding a label.

  requestEditScopes(record: ApiKeyRecord) {
    const current = record.scopes ?? [];
    this.scopesTarget = record;
    this.scopesDraftEnforced = current.filter(isEnforcedScope);
    this.scopesDraftLabels = current.filter((s) => !isEnforcedScope(s)).join(", ");
    this.scopesError = null;
    this.openDialog("EditApiKeyScopesDialog");
  }

  toggleDraftScope(scope: string) {
    this.scopesDraftEnforced = this.scopesDraftEnforced.includes(scope)
      ? this.scopesDraftEnforced.filter((s) => s !== scope)
      : [...this.scopesDraftEnforced, scope];
  }

  setScopesDraftLabels(value: string) {
    this.scopesDraftLabels = value;
  }

  /** The whole list the dialog would store, or null for "no scopes". */
  get scopesDraft(): string[] | null {
    const merged = mergeScopes(this.scopesDraftEnforced, this.scopesDraftLabels);
    return merged.length ? merged : null;
  }

  /**
   * `client-inquiries:answer` is given only at mint (the API refuses adding it
   * to an existing key). The checkbox is locked, but it can still be typed into
   * Labels — so say so here instead of offering a save the API will refuse.
   */
  get scopesDraftMintOnlyError(): string | null {
    const held = this.scopesTarget?.scopes ?? [];
    const draft = this.scopesDraft ?? [];
    // A sole-purpose key's scopes never change (the API refuses any edit).
    const sole = held.find((s) => SOLE_SCOPES.includes(s));
    if (sole) {
      const same =
        new Set(draft).size === new Set(held).size && draft.every((s) => held.includes(s));
      return same
        ? null
        : `This key carries ${sole}, which is its only scope for as long as it lives. Revoke it and mint the key you need.`;
    }
    const adding = draft.find((s) => MINT_ONLY_SCOPES.includes(s) && !held.includes(s));
    if (adding) {
      return `${adding} can only be given when a key is minted, with its client set. Mint a new key for the instance instead.`;
    }
    const addingSole = draft.find((s) => SOLE_SCOPES.includes(s) && !held.includes(s));
    return addingSole
      ? `${addingSole} can only be given when a key is minted, as its only scope. Mint a new key for it instead.`
      : null;
  }

  get canSubmitScopes(): boolean {
    return this.scopesTarget !== null && !this.savingScopes && this.scopesDraftMintOnlyError === null;
  }

  cancelScopes() {
    if (this.savingScopes) return;
    this.scopesTarget = null;
    this.scopesError = null;
  }

  /** @returns true when the patch landed (see submitCreate for why). */
  async submitScopes(): Promise<boolean> {
    const target = this.scopesTarget;
    if (!target || this.savingScopes) return false;
    this.savingScopes = true;
    this.scopesError = null;
    const scopes = this.scopesDraft;
    try {
      await this.apiKeysService?.patch(target.id, { scopes } as never);
      const enforced = (scopes ?? []).filter(isEnforcedScope);
      this.rootStore.notificationStore?.addSuccessNotification(
        enforced.length
          ? `"${target.keyName}" now carries ${enforced.join(", ")}`
          : `Scopes saved for "${target.keyName}" — none of them is enforced`
      );
      runInAction(() => {
        this.scopesTarget = null;
      });
      await this.loadKeys();
      return true;
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.scopesError = err instanceof Error ? err.message : "Failed to save scopes";
      });
      return false;
    } finally {
      runInAction(() => {
        this.savingScopes = false;
      });
    }
  }

  // ---- Revoke / delete ----------------------------------------------------
  //
  // Two dialogs share one body. The framework's dialog title and submit label
  // are static strings on the registration, so "Revoke API key" and "Delete API
  // key" cannot be one dialog re-labelled at runtime — the same reason User
  // Management registers UserFormDialog and UserEditDialog separately.

  requestRevoke(record: ApiKeyRecord) {
    this.confirmAction = { type: "revoke", record };
    this.actionError = null;
    this.openDialog("RevokeApiKeyDialog");
  }

  requestDelete(record: ApiKeyRecord) {
    this.confirmAction = { type: "delete", record };
    this.deleteAcknowledged = false;
    this.actionError = null;
    this.openDialog("DeleteApiKeyDialog");
  }

  setDeleteAcknowledged(value: boolean) {
    this.deleteAcknowledged = value;
  }

  /**
   * Gate on the delete dialog's submit. Delete is irreversible AND the row
   * carries no other trace once gone, while revoke keeps it for the audit trail
   * — so only delete gets the second action. The framework has no destructive
   * button variant, so this is what distinguishes the two dialogs' weight.
   */
  get isDeleteConfirmed(): boolean {
    return this.deleteAcknowledged;
  }

  cancelConfirm() {
    if (this.actioning) return;
    this.confirmAction = null;
    this.deleteAcknowledged = false;
    this.actionError = null;
  }

  /** @returns true when the revoke/delete landed (see submitCreate for why). */
  async executeConfirmedAction(): Promise<boolean> {
    const action = this.confirmAction;
    if (!action || this.actioning) return false;
    this.actioning = true;
    this.actionError = null;
    try {
      if (action.type === "revoke") {
        // The service models revoke as `create({ action: 'revoke', id })`.
        await this.apiKeysService?.create({
          action: "revoke",
          id: action.record.id,
        } as never);
        this.rootStore.notificationStore?.addSuccessNotification(
          `API key "${action.record.keyName}" revoked`
        );
      } else {
        await this.apiKeysService?.remove(action.record.id);
        this.rootStore.notificationStore?.addSuccessNotification(
          `API key "${action.record.keyName}" deleted`
        );
      }
      runInAction(() => {
        this.confirmAction = null;
      });
      await this.loadKeys();
      return true;
    } catch (err) {
      // No toast: the route throws this into the still-open dialog's footer.
      runInAction(() => {
        if (!isSessionEnded(err)) this.actionError = err instanceof Error ? err.message : "API key action failed";
      });
      return false;
    } finally {
      runInAction(() => {
        this.actioning = false;
      });
    }
  }

  // ---- Loading ------------------------------------------------------------

  async loadKeys() {
    const ended = sessionMark(this.rootStore);
    this.loading = true;
    this.error = null;
    try {
      // `find` populates the ServiceStore entity cache, which `keys` reads.
      // 500 is the server's hard cap, not a number picked here — asking for
      // more is silently clamped. `truncated` reports when we came back full.
      // findOrThrow: the store config sets throwOnError, but a swallowed or
      // offline-cache answer still has to read as a failure, not "No API keys
      // yet." — the helper checks errorOnFind whatever came back.
      await findOrThrow(this.apiKeysService, { query: { $limit: API_KEYS_SERVER_LIMIT } });
    } catch (err) {
      if (ended()) return;
      runInAction(() => {
        this.error = loadErrorMessage(err, "Failed to load API keys");
      });
    } finally {
      if (!ended()) {
        runInAction(() => {
          this.loading = false;
        });
      }
    }
  }

  /** Best-effort client directory load so rows show names, not raw UUIDs. */
  async loadClients() {
    if (this.clientsLoaded) return;
    this.clientsLoaded = true;
    try {
      // Populates the clients ServiceStore cache, which `clients` reads.
      await this.clientsService?.find({
        query: { $limit: 500, $sort: { name: 1 } },
      });
    } catch {
      // Non-fatal: rows fall back to the raw clientId.
    }
  }

  async load() {
    await Promise.all([this.loadKeys(), this.loadClients()]);
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.loading = false;
    this.error = null;
    this.statusFilter = "all";
    this.searchQuery = "";
    this.sortKey = "createdAt";
    this.sortDir = "desc";
    this.clientsLoaded = false;
    this.creating = false;
    this.createError = null;
    this.form = { clientId: "", keyName: "", scopes: "", expiresAt: "" };
    this.formEnforcedScopes = [];
    this.scopesTarget = null;
    this.scopesDraftEnforced = [];
    this.scopesDraftLabels = "";
    this.scopesError = null;
    this.savingScopes = false;
    this.createdKey = null;
    this.keyCopied = false;
    this.confirmAction = null;
    this.deleteAcknowledged = false;
    this.actioning = false;
    this.actionError = null;
  }
}

export default ApiKeysStore;
